import { apiClient } from '@/shared/lib/apiClient';
import type {
  AdminIdentityCard,
  IdentityCardDraftInput,
  IdentityCardState,
  AdminGuideReview,
  ApiResponse,
  GuideExperienceRange,
  GuideProfile,
  MarketplaceStatus,
  PaginatedResponse,
  ProfileStatus,
  User,
  VerificationProvider,
  VerificationStatus,
  WaitingOn,
} from '@/types';
import { OnboardingStage } from '@/types';

export interface AdminDashboardStats {
  totalUsers: number;
  totalTravelers: number;
  totalAdmins: number;
  totalGuides: number;
  totalGuideProfiles: number;
  totalDestinations: number;
  totalReviews: number;
  pendingVerifications: number;
  recentTravelerSignups: number;
  guidesByStatus: Record<string, number>;
  /**
   * Environment-driven switches from the server. `canDeleteAccounts` mirrors
   * ADMIN_DELETE_ENABLED — the delete buttons render only when it is true, and
   * the API rejects the call anyway when it is not.
   */
  capabilities?: {
    canDeleteAccounts: boolean;
  };
}

export interface PaginatedResult<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AdminGuideListItem {
  id: string;
  userId: string;
  fullName?: string;
  status: string;
  city?: string;
  state?: string;
  yearsOfExperience?: number;
  currentlyWorkingAsGuide?: boolean;
  guideExperienceRange?: GuideExperienceRange;
  rating?: number;
  reviewCount?: number;
  isVisible: boolean;
  submittedAt?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
  identity?: {
    guideId: string;
    code: string;
    status: string;
    issuedAt: string;
    identityUrl: string;
    cardState?: IdentityCardState;
  } | null;
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    avatar?: string;
    country?: string;
    isActive?: boolean;
    role?: string;
  };
}

/** A row in the guide onboarding pipeline. */
export interface AdminOnboardingItem {
  id: string;
  userId: string;
  fullName?: string;
  city?: string;
  state?: string;
  yearsOfExperience?: number;
  currentlyWorkingAsGuide?: boolean;
  guideExperienceRange?: GuideExperienceRange;
  submittedAt?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
  lifecycle: {
    verificationStatus?: VerificationStatus;
    profileStatus?: ProfileStatus;
    marketplaceStatus?: MarketplaceStatus;
  };
  stage: OnboardingStage;
  waitingOn: WaitingOn;
  verificationProvider?: VerificationProvider;
  verificationReference?: string;
  verificationSentAt?: string;
  verificationDecidedAt?: string;
  /** When the row entered its current stage. */
  waitingSince?: string;
  identity?: AdminGuideListItem['identity'];
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    avatar?: string;
  };
}

export interface OnboardingPipelineResult {
  data: AdminOnboardingItem[];
  pagination: PaginatedResult<AdminOnboardingItem>['pagination'];
  counts: Record<OnboardingStage, number>;
}

/** The server's hard cap on `limit` (`getPaginationParams` clamps above this). */
const MAX_PAGE_SIZE = 100;

/** Safety stop, so a pagination bug cannot spin the browser forever. */
const MAX_EXPORT_PAGES = 50;

export const adminApi = {
  async getDashboard(): Promise<AdminDashboardStats> {
    const response = await apiClient.get<ApiResponse<AdminDashboardStats>>('/admin/dashboard');
    return response.data.data!;
  },

  /**
   * One tab of the guide onboarding pipeline, plus every tab's count.
   *
   * Replaces the old getVerificationQueue/getReviewQueue pair: a stage spans
   * all three lifecycle machines, so it cannot be expressed as a filter on one
   * of them. `counts` covers every stage on each response, so the tab badges
   * and the sidebar badge render without extra requests.
   */
  async getOnboardingPipeline(params?: {
    page?: number;
    limit?: number;
    stage?: OnboardingStage;
    /** Free text over the applicant's name, email and phone. */
    search?: string;
  }): Promise<OnboardingPipelineResult> {
    const response = await apiClient.get<
      PaginatedResponse<AdminOnboardingItem> & {
        counts: Record<OnboardingStage, number>;
      }
    >('/admin/guides/onboarding', { params });

    return {
      data: response.data.data,
      pagination: response.data.pagination,
      counts: response.data.counts,
    };
  },

  /**
   * Every row of one pipeline stage, across all pages.
   *
   * The export needs the whole stage, but the endpoint caps `limit` at 100, so
   * this walks the pages. Sequential rather than parallel: there are only ever
   * a handful, and it gives the caller a truthful progress count.
   */
  async fetchAllOnboarding(params: {
    stage: OnboardingStage;
    search?: string;
    onProgress?: (fetched: number, total: number) => void;
  }): Promise<AdminOnboardingItem[]> {
    const { stage, search, onProgress } = params;
    const query = { stage, limit: MAX_PAGE_SIZE, ...(search ? { search } : {}) };

    const first = await adminApi.getOnboardingPipeline({ ...query, page: 1 });
    // Keyed by id because another admin actioning a row mid-walk shifts the
    // pages under us, which otherwise duplicates or drops one.
    const rows = new Map(first.data.map((row) => [row.id, row]));
    onProgress?.(rows.size, first.pagination.total);

    const lastPage = Math.min(first.pagination.totalPages, MAX_EXPORT_PAGES);
    for (let page = 2; page <= lastPage; page += 1) {
      const next = await adminApi.getOnboardingPipeline({ ...query, page });
      next.data.forEach((row) => rows.set(row.id, row));
      onProgress?.(rows.size, first.pagination.total);
    }

    if (first.pagination.totalPages > MAX_EXPORT_PAGES) {
      throw new Error(
        `This tab has more than ${MAX_EXPORT_PAGES * MAX_PAGE_SIZE} applicants. ` +
          'Narrow it with the search box and export in batches.'
      );
    }

    return [...rows.values()];
  },

  async listGuides(params?: {
    page?: number;
    limit?: number;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResult<AdminGuideListItem>> {
    const response = await apiClient.get<PaginatedResponse<AdminGuideListItem>>('/admin/guides', {
      params,
    });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getGuideForReview(profileId: string): Promise<AdminGuideReview> {
    const response = await apiClient.get<ApiResponse<AdminGuideReview>>(
      `/admin/guides/${profileId}/review`
    );
    return response.data.data!;
  },

  async approveGuide(profileId: string, notes: string): Promise<GuideProfile> {
    const response = await apiClient.patch<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/approve`,
      { notes }
    );
    return response.data.data!;
  },

  async rejectGuide(profileId: string, notes: string): Promise<GuideProfile> {
    const response = await apiClient.patch<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/reject`,
      { notes }
    );
    return response.data.data!;
  },

  async requestMoreInfo(profileId: string, notes: string): Promise<GuideProfile> {
    const response = await apiClient.patch<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/request-info`,
      { notes }
    );
    return response.data.data!;
  },

  async suspendGuide(profileId: string, notes: string): Promise<GuideProfile> {
    const response = await apiClient.patch<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/suspend`,
      { notes }
    );
    return response.data.data!;
  },

  // --- Guide lifecycle ---

  /** Moves a submitted profile into review. Called when the admin opens it. */
  async startGuideReview(profileId: string): Promise<GuideProfile> {
    const response = await apiClient.post<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/start-review`
    );
    return response.data.data!;
  },

  async unsuspendGuide(profileId: string, notes: string): Promise<GuideProfile> {
    const response = await apiClient.patch<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/unsuspend`,
      { notes }
    );
    return response.data.data!;
  },

  // --- Stage 1: background verification ---

  /**
   * Records that the applicant's details were handed to the background-check
   * provider. `reference` is the provider's candidate id, captured at send
   * time so the row can be reconciled later.
   */
  async startVerification(
    profileId: string,
    notes: string,
    reference?: string
  ): Promise<GuideProfile> {
    const response = await apiClient.post<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/verification/start`,
      { notes, reference }
    );
    return response.data.data!;
  },

  /** Grants a retry after a failed check (FAILED -> PENDING). */
  async reopenVerification(profileId: string, notes: string): Promise<GuideProfile> {
    const response = await apiClient.post<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/verification/reopen`,
      { notes }
    );
    return response.data.data!;
  },

  async passVerification(
    profileId: string,
    notes: string,
    reference?: string
  ): Promise<GuideProfile> {
    const response = await apiClient.post<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/verification/pass`,
      { notes, reference }
    );
    return response.data.data!;
  },

  async failVerification(
    profileId: string,
    notes: string,
    reference?: string
  ): Promise<GuideProfile> {
    const response = await apiClient.post<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/verification/fail`,
      { notes, reference }
    );
    return response.data.data!;
  },

  async activateGuideIdentity(profileId: string, notes: string): Promise<GuideProfile> {
    const response = await apiClient.patch<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/identity/activate`,
      { notes }
    );
    return response.data.data!;
  },

  async suspendGuideIdentity(profileId: string, notes: string): Promise<GuideProfile> {
    const response = await apiClient.patch<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/identity/suspend`,
      { notes }
    );
    return response.data.data!;
  },

  async revokeGuideIdentity(profileId: string, notes: string): Promise<GuideProfile> {
    const response = await apiClient.patch<ApiResponse<GuideProfile>>(
      `/admin/guides/${profileId}/identity/revoke`,
      { notes }
    );
    return response.data.data!;
  },

  async getIdentityCard(profileId: string): Promise<AdminIdentityCard> {
    const response = await apiClient.get<ApiResponse<AdminIdentityCard>>(
      `/admin/guides/${profileId}/identity/card`
    );
    return response.data.data!;
  },

  async saveIdentityCardDraft(
    profileId: string,
    input: IdentityCardDraftInput,
    expectedDraftUpdatedAt: string | null
  ): Promise<AdminIdentityCard> {
    const response = await apiClient.put<ApiResponse<AdminIdentityCard>>(
      `/admin/guides/${profileId}/identity/card/draft`,
      { ...input, expectedDraftUpdatedAt }
    );
    return response.data.data!;
  },

  async discardIdentityCardDraft(
    profileId: string,
    expectedDraftUpdatedAt: string | null
  ): Promise<AdminIdentityCard> {
    const response = await apiClient.delete<ApiResponse<AdminIdentityCard>>(
      `/admin/guides/${profileId}/identity/card/draft`,
      { params: expectedDraftUpdatedAt ? { expectedDraftUpdatedAt } : undefined }
    );
    return response.data.data!;
  },

  async publishIdentityCard(
    profileId: string,
    expectedDraftUpdatedAt: string | null
  ): Promise<AdminIdentityCard> {
    const response = await apiClient.post<ApiResponse<AdminIdentityCard>>(
      `/admin/guides/${profileId}/identity/card/publish`,
      { expectedDraftUpdatedAt }
    );
    return response.data.data!;
  },

  async uploadIdentityCardPhoto(profileId: string, file: File): Promise<{ url: string; publicId: string }> {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post<ApiResponse<{ url: string; publicId: string }>>(
      `/admin/guides/${profileId}/identity/card/photo`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data!;
  },

  async listUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
  }): Promise<PaginatedResult<User>> {
    const response = await apiClient.get<PaginatedResponse<User>>('/admin/users', { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async deactivateUser(userId: string): Promise<User> {
    const response = await apiClient.patch<ApiResponse<User>>(`/admin/users/${userId}/deactivate`);
    return response.data.data!;
  },

  /** Soft-deletes a traveler account. Requires ADMIN_DELETE_ENABLED on the API. */
  async deleteUser(userId: string): Promise<void> {
    await apiClient.delete<ApiResponse<unknown>>(`/admin/users/${userId}`);
  },

  /** Soft-deletes a guide profile and its account. Requires ADMIN_DELETE_ENABLED. */
  async deleteGuide(profileId: string): Promise<void> {
    await apiClient.delete<ApiResponse<unknown>>(`/admin/guides/${profileId}`);
  },

  async getCancellationPolicy(): Promise<CancellationPolicySettings> {
    const response = await apiClient.get<ApiResponse<CancellationPolicySettings>>(
      '/admin/settings/cancellation-policy'
    );
    return response.data.data!;
  },

  async updateCancellationPolicy(payload: {
    freeCancelHoursBeforeTravel: number;
    partialCancelHoursBeforeTravel: number;
    partialRefundPercent: number;
  }): Promise<CancellationPolicySettings> {
    const response = await apiClient.put<ApiResponse<CancellationPolicySettings>>(
      '/admin/settings/cancellation-policy',
      payload
    );
    return response.data.data!;
  },
};

export interface CancellationPolicySettings {
  freeCancelHoursBeforeTravel: number;
  partialCancelHoursBeforeTravel: number;
  partialRefundPercent: number;
  policyVersion: number;
  summary: string;
  updatedAt?: string;
}

/**
 * Called with params these are exact query keys; called with none they are the
 * prefix every such query sits under, for invalidation.
 *
 * The params element is dropped when absent on purpose. React Query matches a
 * filter key element by element, so `['admin', 'users', undefined]` compares
 * `undefined` against the live `{ page, limit, role }` object and matches
 * nothing — invalidating after a mutation then did nothing at all and the table
 * only updated on a page refresh.
 */
const scopedKey = (scope: string, params?: Record<string, unknown>) =>
  params === undefined
    ? ([...adminKeys.all, scope] as const)
    : ([...adminKeys.all, scope, params] as const);

export const adminKeys = {
  all: ['admin'] as const,
  dashboard: () => [...adminKeys.all, 'dashboard'] as const,
  onboarding: (params?: Record<string, unknown>) => scopedKey('onboarding', params),
  guides: (params?: Record<string, unknown>) => scopedKey('guides', params),
  review: (id: string) => [...adminKeys.all, 'review', id] as const,
  users: (params?: Record<string, unknown>) => scopedKey('users', params),
  cancellationPolicy: () => [...adminKeys.all, 'cancellation-policy'] as const,
};

/** @deprecated Use adminApi from '@/features/admin/api/admin.api' */
export const adminService = adminApi;
